import { useMemo, useState } from "react";
import { Link, useParams } from "react-router";
import { Select, Tooltip, DatePicker, Input, Button, Drawer } from "antd";
import { SendOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { useFeatureFlag } from "@/hooks/useFeatureFlag";
import { FEATURE_FLAGS } from "@/utils/feature-flag-constants";
import { useTrialPlans } from "@/hooks/useTrialPlans";
import { usePlannedSites } from "@/hooks/usePlannedSites";
import { useUser } from "@/contexts/UserContext";
import { siteLabel } from "./plannedSites";
import type {
  PlannedSite,
  SiteStatus,
  SiteStartupDates,
  SiteComment,
} from "./types";

dayjs.extend(relativeTime);

const STATUS_OPTIONS: { value: SiteStatus; label: string }[] = [
  { value: "candidate", label: "Candidate" },
  { value: "selected", label: "Selected" },
  { value: "rejected", label: "Rejected" },
];
const STATUS_BADGE: Record<SiteStatus, string> = {
  candidate: "bg-[#D8EEF8] text-[#196A9F]",
  selected: "bg-[#CFE5D7] text-[#486563]",
  rejected: "bg-[#FEF2F2] text-[#991B1B]",
};

const STARTUP_FIELDS: {
  key: keyof SiteStartupDates;
  label: string;
  tip?: string;
}[] = [
  // "Identification" is the site-identification milestone DATE — do not label
  // it "Site ID"; users read that as the site identifier column.
  {
    key: "siteIdentification",
    label: "Identification",
    tip: "Site Identification",
  },
  { key: "feasibilitySurvey", label: "Feasibility", tip: "Feasibility Survey" },
  { key: "siteSelection", label: "Selection", tip: "Site Selection" },
  {
    key: "regulatorySubmission",
    label: "Reg. Submission",
    tip: "Regulatory Submission",
  },
  {
    key: "clinicalTrialAgreement",
    label: "CTA",
    tip: "Clinical Trial Agreement",
  },
  { key: "siteActivation", label: "Activation", tip: "Site Activation" },
  { key: "firstPatientIn", label: "FPI", tip: "First Patient In" },
  {
    key: "enrollmentComplete",
    label: "Enroll. Complete",
    tip: "Enrollment Complete",
  },
  {
    key: "lastPatientLastVisit",
    label: "LPLV",
    tip: "Last Patient Last Visit",
  },
];

function StartupDateCell({
  value,
  onChange,
}: {
  value: string | undefined;
  onChange: (v: string) => void;
}) {
  return (
    <DatePicker
      size="small"
      value={value ? dayjs(value) : null}
      onChange={(d) => onChange(d ? d.format("YYYY-MM-DD") : "")}
      placeholder="—"
      style={{ width: 110 }}
      allowClear
    />
  );
}

function CommentsDrawer({
  site,
  open,
  onClose,
  onAddComment,
}: {
  site: PlannedSite | null;
  open: boolean;
  onClose: () => void;
  onAddComment: (siteId: string, comment: SiteComment) => void;
}) {
  const { userInfo } = useUser();
  const [draft, setDraft] = useState("");

  const comments = site?.comments ?? [];

  const authorName =
    [userInfo?.first_name, userInfo?.last_name].filter(Boolean).join(" ") ||
    "You";

  const handlePost = () => {
    if (!site || !draft.trim()) return;
    onAddComment(site.id, {
      id: crypto.randomUUID(),
      author: authorName,
      text: draft.trim(),
      createdAt: new Date().toISOString(),
    });
    setDraft("");
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={site ? `${siteLabel(site)} — Comments` : "Comments"}
      width={420}
    >
      {site && (
        <div className="flex flex-col h-full gap-4">
          <p className="text-[12px] text-[#9CA3AF] -mt-2">
            {site.city}, {site.country}
          </p>

          {/* Comment list */}
          <div className="flex-1 overflow-y-auto flex flex-col gap-3">
            {comments.length === 0 ? (
              <p className="text-[13px] text-[#9CA3AF] italic text-center mt-8">
                No comments yet. Be the first to add one.
              </p>
            ) : (
              comments.map((c) => (
                <div
                  key={c.id}
                  className="rounded-[8px] bg-[#F9FAFB] border border-[#F0F2F4] px-3 py-2.5"
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[12px] font-semibold text-[#1F2933]">
                      {c.author}
                    </span>
                    <Tooltip title={new Date(c.createdAt).toLocaleString()}>
                      <span className="text-[11px] text-[#9CA3AF] cursor-default">
                        {dayjs(c.createdAt).fromNow()}
                      </span>
                    </Tooltip>
                  </div>
                  <p className="text-[13px] text-[#374151] m-0 whitespace-pre-wrap">
                    {c.text}
                  </p>
                </div>
              ))
            )}
          </div>

          {/* Compose */}
          <div className="border-t border-[#F0F2F4] pt-3 shrink-0">
            <p className="text-[11px] font-semibold text-[#6B7280] mb-1.5">
              {authorName}
            </p>
            <Input.TextArea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="Add a comment…"
              rows={3}
              onKeyDown={(e) => {
                if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) handlePost();
              }}
            />
            <div className="flex justify-end mt-2">
              <Button
                type="primary"
                size="small"
                icon={<SendOutlined />}
                className="bg-[#0C98D0] border-none"
                disabled={!draft.trim()}
                onClick={handlePost}
              >
                Post
              </Button>
            </div>
          </div>
        </div>
      )}
    </Drawer>
  );
}

function SiteRow({
  site,
  onUpdate,
  onOpen,
}: {
  site: PlannedSite;
  onUpdate: (changes: Partial<PlannedSite>) => void;
  onOpen: () => void;
}) {
  const su = site.siteStartup ?? {};
  const commentCount = site.comments?.length ?? 0;
  const siteProfileHidden = useFeatureFlag(
    FEATURE_FLAGS.SITE_PROFILE_LINKS_HIDDEN,
  );

  const updateStartup = (field: keyof SiteStartupDates, val: string) =>
    onUpdate({ siteStartup: { ...su, [field]: val } });

  return (
    <tr
      className="border-b border-[#F9FAFB] hover:bg-[#F0F8FD] cursor-pointer"
      onClick={onOpen}
    >
      {/* Site name + comment count */}
      <td
        className="px-3 py-2 sticky left-0 bg-white z-10"
        style={{ minWidth: 180 }}
      >
        {site.institution_key && !siteProfileHidden ? (
          <Link
            to={`/portfolio/site-profiles/${site.institution_key}`}
            className="block text-[13px] font-medium text-[#0C98D0] hover:underline m-0 leading-tight"
            onClick={(e) => e.stopPropagation()}
          >
            {siteLabel(site)}
          </Link>
        ) : (
          <p
            className={`text-[13px] m-0 leading-tight ${
              site.site_name
                ? "font-medium text-[#1F2933]"
                : "italic text-[#6B7280]"
            }`}
          >
            {siteLabel(site)}
          </p>
        )}
        <p className="text-[11px] text-[#9CA3AF] m-0 leading-tight">
          {[
            site.investigator_name,
            [site.city, site.country].filter(Boolean).join(", "),
          ]
            .filter(Boolean)
            .join(" · ")}
        </p>
        {commentCount > 0 && (
          <p className="text-[11px] text-[#0C98D0] m-0 mt-0.5">
            {commentCount} comment{commentCount !== 1 ? "s" : ""}
          </p>
        )}
      </td>

      {/* Status */}
      <td
        className="px-3 py-2"
        style={{ minWidth: 120 }}
        onClick={(e) => e.stopPropagation()}
      >
        <Select
          size="small"
          value={site.status}
          options={STATUS_OPTIONS}
          onChange={(val) => onUpdate({ status: val })}
          style={{ width: 112 }}
          labelRender={({ value }) => (
            <span
              className={`text-[11px] font-semibold rounded-[3px] px-1 ${STATUS_BADGE[value as SiteStatus] ?? ""}`}
            >
              {STATUS_OPTIONS.find((o) => o.value === value)?.label}
            </span>
          )}
        />
      </td>

      {/* Startup milestone dates — stop propagation so date picker clicks don't open the drawer */}
      {STARTUP_FIELDS.map((f) => (
        <td
          key={f.key}
          className="px-3 py-1.5"
          style={{ minWidth: 122 }}
          onClick={(e) => e.stopPropagation()}
        >
          <StartupDateCell
            value={su[f.key]}
            onChange={(v) => updateStartup(f.key, v)}
          />
        </td>
      ))}
    </tr>
  );
}

export default function TrialPlanningSiteStartup() {
  const { planId } = useParams<{ planId: string }>();
  const { getPlan, updatePlan } = useTrialPlans();
  const plan = planId ? getPlan(planId) : undefined;
  const [drawerSite, setDrawerSite] = useState<PlannedSite | null>(null);

  // Line items are named after the site each chosen investigator works at.
  const storedSites = useMemo(() => plan?.sites ?? [], [plan?.sites]);
  const sites = usePlannedSites(storedSites);

  if (!plan) return null;

  const updateSite = (siteId: string, changes: Partial<PlannedSite>) =>
    updatePlan(plan.id, {
      sites: plan.sites.map((s) =>
        s.id === siteId ? { ...s, ...changes } : s,
      ),
    });

  const handleAddComment = (siteId: string, comment: SiteComment) => {
    const site = plan.sites.find((s) => s.id === siteId);
    if (!site) return;
    updateSite(siteId, { comments: [...(site.comments ?? []), comment] });
    // Refresh drawerSite so the comment appears immediately
    setDrawerSite((prev) =>
      prev?.id === siteId
        ? { ...prev, comments: [...(prev.comments ?? []), comment] }
        : prev,
    );
  };

  const sitesWithDates = plan.sites.filter(
    (s) => s.siteStartup && Object.values(s.siteStartup).some(Boolean),
  ).length;

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between mb-3 shrink-0">
        <div>
          <h2 className="text-[15px] font-semibold text-[#1F2933] m-0">
            Sites
          </h2>
          <p className="text-[12px] text-[#9CA3AF] mt-0.5">
            Per-site status, comments, and startup milestone dates.
            {plan.sites.length > 0 && (
              <>
                {" "}
                · {sitesWithDates} / {plan.sites.length} sites with milestone
                dates
              </>
            )}
          </p>
        </div>
      </div>

      {plan.sites.length === 0 ? (
        <div className="flex-1 flex flex-col items-center justify-center rounded-[8px] border border-dashed border-[#D1D5DB] bg-white">
          <p className="text-[14px] text-[#6B7280] text-center px-6">
            Add sites from the Investigators tab first.
          </p>
        </div>
      ) : (
        <div className="flex-1 min-h-0 rounded-[8px] border border-[#F0F2F4] bg-white overflow-auto">
          <table className="w-full text-left" style={{ minWidth: 1200 }}>
            <thead>
              <tr className="bg-[#FAFAFA] border-b border-[#F0F2F4]">
                <th className="px-3 py-2 text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-wide sticky left-0 bg-[#FAFAFA] z-10">
                  Site
                </th>
                <th className="px-3 py-2 text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-wide">
                  Status
                </th>
                {STARTUP_FIELDS.map((f) => (
                  <th
                    key={f.key}
                    className="px-3 py-2 text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-wide whitespace-nowrap"
                  >
                    {f.tip ? (
                      <Tooltip title={f.tip}>
                        <span className="underline decoration-dotted cursor-help">
                          {f.label}
                        </span>
                      </Tooltip>
                    ) : (
                      f.label
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sites.map((site) => (
                <SiteRow
                  key={site.id}
                  site={site}
                  onUpdate={(c) => updateSite(site.id, c)}
                  onOpen={() => setDrawerSite(site)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      <CommentsDrawer
        site={drawerSite}
        open={drawerSite !== null}
        onClose={() => setDrawerSite(null)}
        onAddComment={handleAddComment}
      />
    </div>
  );
}
